/**
 * comboAnswers — reading the CLOSED half of a combo (quant + why) answer the way a
 * person reads it.
 *
 * Relocated verbatim out of `reportv2/ledger.ts` when the v2 report render chain was
 * deleted: `InsightCard` and `QuickStudyOpenEndCard` are the two live consumers, and
 * both only ever needed these three symbols. Pure, React-free, survey-agnostic: only
 * the generic SurveyJS scale/choice SHAPE is read, never a specific survey, question
 * id or answer value.
 */

/** The verbatim ("why") field a combo run writes its open half under. */
export const COMBO_WHY_FIELD = 'qs_main_why'

const QS_MAIN_FIELD = 'qs_main'

type ScaleQuestion = { rateMax?: unknown; rateValues?: unknown; choices?: unknown }

// The QUANT field a combo response answers under: the question's own `name` when the response
// carries a value for it, else the QS default ("qs_main").
function quantField(question: unknown, surveyData: Record<string, unknown>): string {
  const name = (question as { name?: string } | null | undefined)?.name
  if (name && surveyData[name] !== undefined) return name
  return QS_MAIN_FIELD
}

// One choice option's display text. SurveyJS allows a bare string OR `{value, text}`, so both
// shapes resolve; the stored value is the fallback only when a choice carries no label.
function choiceText(choice: unknown): { value: string; text: string } {
  if (choice && typeof choice === 'object') {
    const c = choice as { value?: unknown; text?: unknown }
    const value = String(c.value ?? '').trim()
    return { value, text: String(c.text ?? '').trim() || value }
  }
  const raw = String(choice ?? '').trim()
  return { value: raw, text: raw }
}

// The rating-scale ceiling for a per-quote score badge ("score/scaleMax"), read from the SAME
// question fields the quant exhibit already uses: SurveyJS `rateMax` (NPS → 10, 5-pt → 5), else
// the rate-values / choices count, else a 5-pt default. Canonical here so the chat badge and the
// card chip read one scale.
export function comboScaleMax(question: ScaleQuestion | null | undefined): number {
  const q = question as ScaleQuestion | null | undefined
  const rateMax = Number(q?.rateMax)
  if (Number.isFinite(rateMax) && rateMax > 0) return rateMax
  if (Array.isArray(q?.rateValues) && q.rateValues.length > 0) return q.rateValues.length
  if (Array.isArray(q?.choices) && q.choices.length > 0) return q.choices.length
  return 5
}

// --------------------------------------------------------------------------- //
// What the twin ANSWERED on the closed half of a combo, written the way a person reads it: the
// chosen option's LABEL ("B — Depth"), a rating as "9/10", a boolean as its yes/no wording. Shown
// beside the quote so a why can be read against the choice it explains.
//
// NEVER the raw stored value. A bare option code is exactly the junk this path exists to keep out
// of the card. Unresolvable → "" and the quote simply carries no answer chip.
// --------------------------------------------------------------------------- //
export function comboAnswerLabel(
  surveyData: Record<string, unknown>,
  question: unknown,
  itemId?: string,
): string {
  let raw = surveyData[quantField(question, surveyData)]
  if (raw == null) return ''
  if (typeof raw === 'object' && !Array.isArray(raw)) {
    // Monadic combo: the closed answer is stored keyed by the version the twin saw
    // ({item_id: score}). Unwrap that twin's own entry; a multi-entry object with no item_id
    // to pick by is unattributable, so emit nothing rather than guess.
    const entries = Object.values(raw as Record<string, unknown>)
    const keyed = itemId ? (raw as Record<string, unknown>)[itemId] : undefined
    raw = keyed !== undefined ? keyed : entries.length === 1 ? entries[0] : null
    if (raw == null || (typeof raw === 'object' && !Array.isArray(raw))) return ''
  }

  const q = question as (ScaleQuestion & { labelTrue?: unknown; labelFalse?: unknown }) | null | undefined
  const choices = Array.isArray(q?.choices) ? q.choices : []
  const picked = Array.isArray(raw) ? raw : [raw]

  // Choice questions (single or multi): every picked value must resolve, or we emit nothing —
  // a partial render would be as unreadable as the code itself.
  if (choices.length > 0 && picked.length > 0) {
    const labels = picked.map((v) => {
      const hit = choices.find((c) => choiceText(c).value === String(v).trim())
      return hit ? choiceText(hit).text : ''
    })
    if (labels.every((l) => l.length > 0)) return labels.join(', ')
  }

  if (typeof raw === 'boolean') {
    const yes = String(q?.labelTrue ?? '').trim() || 'Yes'
    const no = String(q?.labelFalse ?? '').trim() || 'No'
    return raw ? yes : no
  }

  // Rating / numeric scale. Guarded on the ORIGINAL scalar so an array never coerces (Number(['7'])
  // is 7) and a blank string never reads as zero.
  if (!Array.isArray(raw)) {
    const text = String(raw).trim()
    const n = Number(text)
    if (text.length > 0 && Number.isFinite(n)) return `${n}/${comboScaleMax(q)}`
  }

  return ''
}
